import React, { FC, useEffect } from 'react';
import VideoJsPlayer from 'video.js';
import { isIOS, isSafari } from 'react-device-detect';
import { select } from '~/lib/select';
import {
  notifyLearnerVideoActivity,
  VideoActivityType
} from '~/shell/PostMessage';
import { TrainingState, useTrainingStore } from '~/training/state';
import { MediaKind, MediaPlayerStore } from './MediaPlayerStore';
import { useGlobalMediaStore } from './GlobalMediaProvider';

import { createContext, useCallback, useContext, useRef } from 'react';
import { useStore, create, StateSelector, StoreApi } from 'zustand';

import { normalizeMediaUrl as fixSrc } from './mediaSource';

/**
 * 52-byte silent WAV, used only when the audio element has nothing loaded yet.
 * A data URL is playable the instant it is assigned, so the gesture is never
 * wasted waiting for a download.
 */
const SILENT_AUDIO =
  'data:audio/wav;base64,UklGRiwAAABXQVZFZm10IBAAAAABAAEAQB8AAIA+AAACABAAZGF0YQgAAAAAAAAAAAAAAA==';

function isJSON(src: string) {
  try {
    JSON.parse(src);
  } catch (e) {
    return false;
  }
  return true;
}

const notifyVideoActivity = (activityType: VideoActivityType) => {
  const { learnerTrainingId, items, activeItemPosition } =
    useTrainingStore.getState();

  if (!learnerTrainingId) {
    return;
  }

  const sequence = items.find(
    i => i.kind === 'sequence' && i.position === activeItemPosition
  );

  notifyLearnerVideoActivity(
    learnerTrainingId,
    activityType,
    sequence?.id ?? ''
  );
};

const NARRATION_WAIT_TIMEOUT_MS = 15000;

export const createStore = () => {
  let recoverable = false;
  let loadedPlayer: Parameters<MediaPlayerStore['setPlayer']>[0] | undefined;
  let waitTimer: ReturnType<typeof setTimeout> | undefined;

  const clearWait = () => {
    clearTimeout(waitTimer);
    waitTimer = undefined;
  };

  const failMedia = () => {
    clearWait();
    replacingSrc = false;
    // Required narration must recover or finish; a failed load is not completion.
    store.setState({
      status: recoverable ? 'loading' : 'nomedia',
      loadFailed: true
    });
  };

  const watchWait = () => {
    const { status, loadFailed } = store.getState();
    if (
      !recoverable ||
      waitTimer ||
      loadFailed ||
      status === 'paused' ||
      status === 'done'
    )
      return;
    waitTimer = setTimeout(() => {
      const player = store.getState().player();
      if (player) {
        failMedia();
        player.pause();
      }
    }, NARRATION_WAIT_TIMEOUT_MS);
  };
  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
  // @ts-ignore
  const players: { [key in MediaKind]?: VideoJsPlayer } = {};

  // WebKit grants playback per media element, and only to an element that has
  // already been played from inside a user gesture. Narration starts from a
  // timer instead, so on iOS it is rejected on every slide until `unlockAudio`
  // has run once during a tap.
  let audioUnlocked = false;

  // Set while the unlock play/pause round-trip runs, so it cannot leak a
  // phantom `playing` / `done` status into the sequence the learner is
  // navigating to.
  let unlockingAudio = false;

  // Set while `loadMedia` assigns a new `src` on an element that already had
  // one. The browser aborts the in-flight fetch for the old resource — that
  // abort is expected and must not mark the new narration as `nomedia`.
  let replacingSrc = false;

  // This handler is defined here so it can be removed from the text track and prevent memory leaks
  const cueChangeHandler = () => {
    const player = store.getState().player();
    if (!player) return;

    // Force track mode for Firefox
    forceTrackMode(player);

    const tracks = player.textTracks();

    for (let i = 0; i < tracks.length; i++) {
      const t = tracks[i];
      // Find the first subtitles track
      if (t.kind !== 'subtitles') continue;

      // For some reason, this doesn't do anything when creating the text track but adding
      // it here does hide the default captions
      t.mode = 'hidden';

      const captions: string[] = [];

      if (t.activeCues && t.activeCues.length > 0) {
        for (let i = 0; i < t.activeCues.length; i++) {
          const cue = t.activeCues[i];
          if (cue && cue.text) {
            //it is hot fix
            const res = cue.text.replace(/"/gi, '');
            if (res) {
              if (!isJSON(res)) {
                captions.push(res);
              }
            }
          }
        }
      }

      store.setState({ captions });
      return;
    }
  };

  const forceTrackMode = (
    player: Parameters<MediaPlayerStore['setPlayer']>[0]
  ) => {
    const tracks = player.textTracks();
    for (let i = 0; i < tracks.length; i++) {
      const track = tracks[i];
      if (track.kind === 'subtitles' && track.mode !== 'hidden') {
        track.mode = 'hidden';
      }
    }
  };

  /**
   * Removes all text tracks from the player
   */
  const clearTextTracks = () => {
    const player = store.getState().player();
    if (!player) return;

    const remoteTracks = player.remoteTextTracks();

    for (let i = remoteTracks.length - 1; i >= 0; i--) {
      const track = remoteTracks[i];

      track.removeEventListener('cuechange', cueChangeHandler);
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      player.removeRemoteTextTrack(track as any);
    }

    const videoElement = player.el().querySelector('video');

    if (videoElement) {
      const textTracks = videoElement.textTracks;
      for (let i = textTracks.length - 1; i >= 0; i--) {
        const track = textTracks[i];
        if (track.kind === 'subtitles') {
          track.mode = 'disabled';
        }
      }
    }
  };

  const store = create<MediaPlayerStore>((set, get) => ({
    status: 'nomedia',
    completed: false,
    loadFailed: false,
    progress: 0,
    currentTime: 0,
    mediaSrc: '',
    mediaKind: 'none',
    subtitlesSrc: '',
    captions: [],
    playbackSpeed: 1,
    snapshotUrl: '',

    setPlayer: (player, kind) => {
      // just in case, pause any media already playing
      try {
        players[kind]?.pause();
      } catch (error) {
        // Silently ignore player operation errors
      }

      players[kind] = player;
      if (!player) return;

      // The unlock round-trip drives this element directly. Its events must not
      // reach the store, or they surface as a phantom status on the next slide.
      const isUnlockNoise = () => unlockingAudio && kind === 'audio';

      // A replaced or disposed player keeps its listeners (`removePlayer` only
      // frees the slot), so without this it would still be writing into the
      // store from the slide the learner has already left.
      const isStale = () => players[kind] !== player;

      // A slide can keep an audio and a video element busy at the same time,
      // while `status` describes only the media loaded last.
      const ownsCurrentMedia = () => !isStale() && kind === get().mediaKind;

      player.on('error', function () {
        if (isUnlockNoise() || !ownsCurrentMedia()) return;
        failMedia();
      });

      // `abort` and `stalled` are only treated as fatal (`nomedia`) while the
      // media is still LOADING — i.e. it never became playable. That preserves
      // the original safety net: a video that fails to load (especially a
      // `blocking` one) is marked `nomedia` so the learner isn't trapped.
      //
      // Once the player is past loading (`ready`/`playing`/`paused`), these same
      // events fire NORMALLY during a seek (HLS/VHS aborts the in-flight segment
      // request and stalls until the segment at the new position is downloaded).
      // Killing the player then caused the freeze on every restore-seek, so we
      // ignore them in that case and let it recover via `canplay`/`playing`.
      //
      // They are also routine on a slow connection, so only the element that
      // owns the current media may report it: a narration stalling on mobile
      // used to leave the video of the same slide stuck without a play button.
      player.on('abort', function () {
        if (isUnlockNoise() || !ownsCurrentMedia()) return;
        if (replacingSrc && get().status === 'loading') return;
        if (recoverable) watchWait();
        else if (get().status === 'loading') failMedia();
      });

      // Unlike `abort`, do not ignore `stalled` during a src swap: a genuine
      // stall on the newly assigned narration (slow mobile) must still fall
      // through to `nomedia`, otherwise ComboButton stays blank forever.
      player.on('stalled', function () {
        if (isUnlockNoise() || !ownsCurrentMedia()) return;
        if (recoverable) watchWait();
        else if (get().status === 'loading') failMedia();
      });

      player.on('waiting', () => {
        if (isUnlockNoise() || !ownsCurrentMedia()) return;
        watchWait();
      });
      player.on('playing', () => {
        if (isUnlockNoise() || !ownsCurrentMedia() || get().loadFailed) return;
        clearWait();
        if (recoverable) set({ status: 'playing' });
      });

      player.on('loadedmetadata', () => {
        if (isUnlockNoise() || !ownsCurrentMedia() || get().loadFailed) return;
        const { status, mediaSrc, mediaKind } = get();
        const canReady =
          status === 'loading' ||
          (status === 'nomedia' && Boolean(mediaSrc) && mediaKind === kind);
        if (!canReady) return;
        replacingSrc = false;
        set({ status: 'ready', loadFailed: false });
      });

      player.on('canplay', () => {
        if (isUnlockNoise() || !ownsCurrentMedia()) return;
        clearWait();
        const { status, mediaSrc, mediaKind } = get();
        const canReady =
          status === 'loading' ||
          (status === 'nomedia' && Boolean(mediaSrc) && mediaKind === kind);
        if (!canReady) return;
        replacingSrc = false;
        set({ status: 'ready', loadFailed: false });
      });
      player.on('play', () => {
        if (isUnlockNoise() || !ownsCurrentMedia() || get().loadFailed) return;
        set({ status: 'playing' });
        watchWait();
        if (kind === 'video') {
          notifyVideoActivity('video_started');
        }
        // Force cueChangeHandler execution for Firefox
        setTimeout(() => {
          forceTrackMode(player);
          cueChangeHandler();
        }, 100);
      });
      player.on('pause', () => {
        if (isUnlockNoise() || !ownsCurrentMedia()) return;
        if (get().status === 'playing') {
          clearWait();
          set({ status: 'paused' });
          if (kind === 'video') {
            notifyVideoActivity('video_paused');
          }
        }
      });
      player.on('ended', () => {
        // Native media pauses before ended. Accept that final pause, while
        // ignoring late completion from failed, loading or replaced media.
        if (
          isUnlockNoise() ||
          !ownsCurrentMedia() ||
          (get().status !== 'playing' && get().status !== 'paused')
        )
          return;
        clearWait();
        set({ status: 'done', completed: true });
        if (kind === 'video') {
          notifyVideoActivity('video_ended');
        }
      });
      player.on('timeupdate', () => {
        if (!ownsCurrentMedia()) return;
        // Don't track the current time when not playing
        if (get().status !== 'playing') return;
        try {
          if (player.currentTime() > get().currentTime) clearWait();
          set({
            progress: player.currentTime() / player.duration(),
            currentTime: player.currentTime()
          });

          // Periodically check for captions in Firefox
          if (player.currentTime() % 1 < 0.1) {
            cueChangeHandler();
          }
        } catch (error) {
          // Silently ignore player operation errors
        }
      });
    },

    removePlayer: (player, kind) => {
      // Clear the actual element being disposed, rather than a hook's borrowed
      // current player. Background audio must not release a surviving video.
      const global = useGlobalMediaStore.getState();
      if (global.player === player) global.setPlayer();
      if (players[kind] !== player) return;
      if (kind === get().mediaKind) clearWait();
      delete players[kind];
    },

    player: () => players[get().mediaKind],

    play: async () => {
      // Note: this may not work if the user has not interacted with the page

      const player = get().player();
      const mediaSrc = get().mediaSrc;
      const playPromise = player?.play();
      if (playPromise !== undefined) {
        playPromise
          .then(() => {
            if (get().player() !== player || get().mediaSrc !== mediaSrc)
              return;
            player?.playbackRate(get().playbackSpeed);
          })
          .catch((error: { name?: string; message?: string }) => {
            if (get().player() !== player || get().mediaSrc !== mediaSrc)
              return;
            // Interruption errors are expected and not worth reporting, but a
            // blocked autoplay is the one failure we need to see while testing
            // the gesture unlock on real devices.
            if (
              recoverable &&
              !get().loadFailed &&
              error?.name === 'NotAllowedError'
            ) {
              clearWait();
              set({ status: 'ready' });
            }
            if (error?.name === 'NotAllowedError') {
              console.warn(
                '[media] playback blocked by the browser autoplay policy:',
                error.message
              );
            }
          });
      }
    },

    retry: () => {
      const { loadFailed, mediaSrc, player: getPlayer, play } = get();
      const player = getPlayer();
      if (!loadFailed || !mediaSrc || !player) return;

      // loadMedia intentionally skips an unchanged source. Reload this element
      // explicitly, keeping play inside the same gesture for WebKit.
      set({ loadFailed: false, status: 'loading' });
      replacingSrc = true;
      player.error(null);
      player.load();
      watchWait();
      play();
    },

    unlockAudio: () => {
      // Only WebKit needs this. Everywhere else the delayed autoplay already
      // works, and there is no reason to touch a working player.
      if (!isIOS && !isSafari) return;
      if (audioUnlocked || unlockingAudio) return;

      const player = players.audio;
      if (!player) return;

      // `status` and `mediaSrc` describe whichever media was loaded last, so on
      // a video slide they say nothing about the audio element being unlocked.
      // Read its own state instead.
      //
      // An element that is already playing has cleared the policy on its own,
      // so leave it alone and just confirm the permission with a no-op `play()`.
      let wasPlaying = false;
      let wasMuted = false;
      let hasSource = false;
      try {
        wasPlaying = !player.paused();
        wasMuted = Boolean(player.muted());
        // `src()` is filled synchronously on assignment, `currentSrc()` only
        // once the browser has picked the resource — a narration that is still
        // loading has just the former.
        hasSource = Boolean(player.src() || player.currentSrc());
      } catch (error) {
        // Silently ignore player operation errors
      }

      const finish = (unlocked: boolean) => {
        try {
          if (!wasPlaying) {
            player.pause();
            player.muted(wasMuted);
          }
        } catch (error) {
          // Silently ignore player operation errors
        }

        audioUnlocked = unlocked;

        // `pause` / `ended` are delivered a tick after we stop the element, so
        // keep suppressing them briefly. Narration cannot legitimately start
        // within this window: it is always at least one navigation away.
        setTimeout(() => {
          unlockingAudio = false;
        }, 100);
      };

      unlockingAudio = true;

      try {
        if (!wasPlaying) {
          player.muted(true);
          // Only when the element has nothing to play at all. A narration that
          // is merely still loading must not be replaced, or the store would
          // keep pointing at a source the element no longer holds.
          if (!hasSource) {
            player.src({ src: SILENT_AUDIO, type: 'audio/wav' });
          }
        }

        const playPromise = player.play();

        if (playPromise === undefined) {
          finish(true);
          return;
        }

        // A rejection leaves `audioUnlocked` false on purpose, so the next
        // gesture gets another attempt.
        playPromise.then(() => finish(true)).catch(() => finish(false));
      } catch (error) {
        finish(false);
      }
    },

    pause: () => {
      try {
        get().player()?.pause();
      } catch (error) {
        // Silently ignore player operation errors
      }
    },

    toggle: () => {
      const { status, play, pause } = get();
      if (status === 'playing') pause();
      else if (status === 'paused' || status === 'ready') play();
    },

    reset: () => {
      clearWait();
      recoverable = false;
      loadedPlayer = undefined;
      const player = get().player();
      replacingSrc = false;
      try {
        player?.pause();
      } catch (error) {
        // Silently ignore player operation errors
      }
      player?.currentTime(0);
      set({
        progress: 0,
        currentTime: 0,
        status: 'nomedia',
        completed: false,
        loadFailed: false,
        mediaSrc: '',
        mediaKind: 'none',
        subtitlesSrc: '',
        captions: [],
        snapshotUrl: ''
      });
      clearTextTracks();
    },

    restart: () => {
      const player = get().player();
      const mediaSrc = get().mediaSrc;
      player
        ?.play()
        ?.then(() => {
          if (get().player() !== player || get().mediaSrc !== mediaSrc) return;
          player?.currentTime(0);
          player?.pause();
        })
        .catch(() => {
          // Silently ignore play interruption errors
        });
    },

    loadMedia: (
      mediaSrc,
      subtitlesSrc,
      kind,
      autoplay,
      playDelay = 0,
      snapshotUrl,
      canRecover = false
    ) => {
      if (typeof mediaSrc === 'string') {
        mediaSrc = fixSrc(mediaSrc || '');
      } else if (mediaSrc) {
        mediaSrc = mediaSrc.map(({ src, type }) => ({
          src: fixSrc(src),
          type
        }));
      }
      if (Array.isArray(mediaSrc) && mediaSrc.length === 0) mediaSrc = '';
      const sourceChanged =
        JSON.stringify(mediaSrc || '') !== JSON.stringify(get().mediaSrc) ||
        Boolean(mediaSrc && kind !== get().mediaKind);
      // A remounted native element still needs its source, even after completion.
      if (sourceChanged || (mediaSrc && players[kind] !== loadedPlayer)) {
        clearWait();
        recoverable = canRecover;
        try {
          players.audio?.pause();
          players.video?.pause();
        } catch (error) {
          // Silently ignore player operation errors
        }
        const player = players[kind];

        if (!mediaSrc) {
          set({
            status: 'nomedia',
            completed: false,
            loadFailed: false,
            mediaSrc: '',
            mediaKind: 'none',
            snapshotUrl: ''
          });
        } else {
          set({
            status: 'loading',
            completed: sourceChanged ? false : get().completed,
            currentTime:
              sourceChanged || get().completed ? 0 : get().currentTime,
            progress: sourceChanged || get().completed ? 0 : get().progress,
            loadFailed: false,
            mediaSrc,
            mediaKind: kind,
            snapshotUrl
          });
          if (!player) {
            set({
              status: 'nomedia',
              mediaSrc: '',
              mediaKind: 'none',
              snapshotUrl: ''
            });
          }
          if (snapshotUrl) player?.poster(fixSrc(snapshotUrl));
          replacingSrc = true;
          loadedPlayer = player;
          player?.src(
            typeof mediaSrc === 'string'
              ? {
                  src: mediaSrc,
                  type: kind === 'audio' ? 'audio/mpeg' : 'video/mp4'
                }
              : mediaSrc
          );
          player?.currentTime(get().currentTime);
          watchWait();

          const urlParams = new URLSearchParams(window.location.search);
          const dontPlayAudioAndVideo = urlParams.has('dontPlayAudioAndVideo');

          if (autoplay && !dontPlayAudioAndVideo) {
            setTimeout(() => {
              if (
                get().player() !== player ||
                get().mediaSrc !== mediaSrc ||
                get().loadFailed
              )
                return;
              get().play();
            }, playDelay);
          }
        }
      }
      get().loadSubtitles(subtitlesSrc);
      clearTextTracks();
    },

    loadSubtitles: src => {
      const url = fixSrc(src || '');
      set({ subtitlesSrc: url });

      clearTextTracks();

      const player = get().player();
      if (!url || !player) return;

      // Add a small delay for Firefox to ensure proper track handling
      setTimeout(() => {
        const currentPlayer = get().player();
        if (!currentPlayer) return;

        const track = currentPlayer.addRemoteTextTrack(
          {
            kind: 'subtitles',
            src: url,
            mode: 'hidden',
            default: true,
            srclang: 'en',
            label: 'English'
          },
          true
        );

        // Ensure the track is properly configured for Firefox
        if (track) {
          track.addEventListener('cuechange', cueChangeHandler);

          // Add additional event listeners for Firefox compatibility
          track.addEventListener('load', () => {
            // Force mode setting for Firefox compatibility
            setTimeout(() => {
              if (track.mode !== 'hidden') {
                track.mode = 'hidden';
              }
              // Trigger cueChangeHandler manually for Firefox
              setTimeout(() => {
                cueChangeHandler();
              }, 50);
            }, 100);
          });

          // Force initial cueChangeHandler call
          setTimeout(() => {
            cueChangeHandler();
          }, 200);
        }
      }, 50);
    },

    setSpeed: speed => {
      get().player()?.playbackRate(speed);
      set({ playbackSpeed: speed });
    },

    seek: span => {
      const player = get().player();
      if (!player) return;
      player.currentTime(player.currentTime() + span);
    }
  }));

  return store;
};

const StoreContext = createContext<StoreApi<MediaPlayerStore> | null>(null);

const selector = (s: TrainingState) =>
  select(s, 'playbackSpeed', 'featureFocus');
const mediaSelector = (s: MediaPlayerStore) => select(s, 'setSpeed', 'pause');

const MediaPlayerHelper = () => {
  const store = useContext(StoreContext);
  if (!store) {
    throw new Error('Missing StoreProvider');
  }

  const { playbackSpeed, featureFocus } = useTrainingStore(selector);
  const { setSpeed, pause } = useStore(store, mediaSelector);

  useEffect(() => {
    // Update playback speed when the settings change
    setSpeed(playbackSpeed);
  }, [playbackSpeed]);

  useEffect(() => {
    if (featureFocus) {
      // Auto pause playback when window loses focus
      window.addEventListener('blur', pause);
      return () => window.removeEventListener('blur', pause);
    }
  }, []);

  return null;
};

interface MediaPlayerProviderProps {
  children: React.ReactNode;
}

export const MediaPlayerProvider: FC<MediaPlayerProviderProps> = ({
  children
}) => {
  const storeRef = useRef<StoreApi<MediaPlayerStore> | null>(null);
  if (!storeRef.current) {
    storeRef.current = createStore();
  }
  return (
    <StoreContext.Provider
      value={storeRef.current as StoreApi<MediaPlayerStore>}
    >
      <MediaPlayerHelper />
      {children}
    </StoreContext.Provider>
  );
};

/**
 * Gesture-safe access to `unlockAudio`. The accessibility settings are also
 * rendered during onboarding, which sits outside the provider, so this degrades
 * to a no-op there instead of throwing.
 */
export const useUnlockAudio = () => {
  const store = useContext(StoreContext);

  return useCallback(() => {
    store?.getState().unlockAudio();
  }, [store]);
};

export const useMediaPlayerStore = <T = MediaPlayerStore,>(
  selector: StateSelector<MediaPlayerStore, T> = state => state as unknown as T,
  shallow?: (a: T, b: T) => boolean
): T => {
  const store = useContext(StoreContext);
  if (!store) {
    throw new Error('Missing StoreProvider');
  }

  if (shallow) {
    return useStore(store, selector, shallow) as T;
  }
  return useStore(store, selector) as T;
};
